import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  TabContent,
  Table,
  TabPane,
  Spinner,
  CardFooter,
} from 'reactstrap';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import dlIcon from '../../../assets/icons/dlicon2.png';
import reassignIcon from '../../../assets/icons/reassignicon.png';
import {
  ASSIGNED_TO_TICKETS_HEADERS,
  MY_TICKETS_HEADERS,
  ALL_TICKETS_HEADERS,
  TICKET_STATUS_BADGE_STYLES,
  TICKET_TABS,
  TICKET_MAIN_STATUS,
} from './constants';
import CreateTicket from '../../../components/Ticket/CreateTicket/CreateTicket';
import './HRTicketListing.scss';
import { ReassignTicket } from '../../../components/Ticket';
import useTableHook from '../../../components/hooks/useTableHook';
import {
  FilterDropdown,
  TableSortDisplayArrow,
  Badge,
  ModalMultiWrapper,
  TableFilterBubbles,
  DatePicker,
  TablePagination,
} from '../../../components/Common';
import { getAuthUser } from '../../../utils/auth.util';
import { ExportUtil, FileUtil } from '../../../utils';
import {
  fetchTicketDetailsListing,
  updateTicketList,
} from '../../../store/apis/ticketService';
import { fetchTableColumnValues } from '../../../store/apis/commonService';
import { ROLE_NAMES } from '../../../common/constant';

const getActiveTabFromQuery = queryTab => {
  if (queryTab === '1') {
    return TICKET_TABS.ASSIGNED_TO_ME;
  }
  if (queryTab === '2') {
    return TICKET_TABS.ALL_TICKETS;
  }
  if (queryTab === '3') {
    return TICKET_TABS.MY_TICKETS;
  }
  return TICKET_TABS.ASSIGNED_TO_ME;
};

const initialPageRequestTicketConfig = queryTab => {
  if (queryTab === '1') {
    return {
      assignedTo: getAuthUser()?.employeeId,
      requestor: null,
    };
  }
  if (queryTab === '2') {
    return {
      assignedTo: null,
      requestor: null,
    };
  }
  if (queryTab === '3') {
    return {
      requestor: getAuthUser()?.employeeId,
      assignedTo: null,
    };
  }
  return {
    assignedTo: getAuthUser()?.employeeId,
    requestor: null,
  };
};

function HRTicketListing() {
  // Set the document title for the page
  document.title = 'Tickets | Envision Daikin';
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(
    getActiveTabFromQuery(queryParams.get('t')) || TICKET_TABS.ASSIGNED_TO_ME,
  );
  const [activeStatus, setActiveStatus] = useState('open');
  const [createTicketOffcanvas, setCreateTicketOffcanvas] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modelName, setModelName] = useState('');
  const [tableHeaders, setTableHeaders] = useState(ASSIGNED_TO_TICKETS_HEADERS);
  const roleName = getAuthUser()?.role?.Name;

  const checkIfAdmin = () =>
    roleName === ROLE_NAMES.ADMIN || roleName === ROLE_NAMES.HR_ADMIN;

  useEffect(() => {
    let tempTableHeader = {};
    if (activeTab === TICKET_TABS.ASSIGNED_TO_ME) {
      tempTableHeader = ASSIGNED_TO_TICKETS_HEADERS;
    } else if (activeTab === TICKET_TABS.MY_TICKETS) {
      tempTableHeader = MY_TICKETS_HEADERS;
    } else if (activeTab === TICKET_TABS.ALL_TICKETS) {
      tempTableHeader = ALL_TICKETS_HEADERS;
    }

    if (activeStatus === 'open') {
      const { StatusLastDate, ...rest } = tempTableHeader; // hide for OPEN
      setTableHeaders(rest);
    } else {
      const { OpenSinceDays, ...rest } = tempTableHeader; // hide for CLOSED
      setTableHeaders(rest);
    }

    queryClient.invalidateQueries({
      queryKey: ['ticketListing'],
    });
  }, [activeTab, activeStatus]);

  // Table Hook for managing ticket listing data
  const {
    data,
    columnData,
    pageRequest,
    setPageRequest,
    externalFilters,
    setExternalFilters,
    applyFilter,
    columnName,
    setColumnName,
    activeRows,
    handleRowCheck,
    selectAllRows,
    resetFilters,
    handleManualSort,
    checkColumnIfSorted,
    applyExternalFilters,
    resetActiveRows,
    tableDataMutation,
    isLoadingData,
    isLoadingColumn,
    clearSingleFilterValue,
    nextPage,
    prevPage,
    getPageSize,
    setPageSize,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      pageNumber: 1,
      pageSize: 50,
      sortColumn: 'TicketNo',
      sortDirection: 'DESC',
      filters: [],
      ticketStatus: TICKET_MAIN_STATUS.OPEN,
      ...initialPageRequestTicketConfig(queryParams.get('t')),
    },
    fetchFn: fetchTicketDetailsListing,
    fetchColumnFn: fetchTableColumnValues,
    queryKeyPrefix: 'ticketListing',
    columnKeyPrefix: 'ticketListingColumn',
    tableName: 'TicketListing',
    defaultExternalFilters: {},
  });

  // Update Ticke List Mutation for Reassigning Tickets
  const updateTicketListMutation = useMutation({
    mutationFn: payload => updateTicketList(payload),
  });

  // Toggle Offcanvas for Create Ticket
  const toggleCreateTicketOffcanvas = () => {
    setCreateTicketOffcanvas(!createTicketOffcanvas);
  };

  // Toggle between Assigned To Me, All Tickets, and My Tickets
  const toggleTab = tab => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      resetFilters();
    }
  };

  // Toggle between Open and Closed status
  const toggleStatus = status => {
    if (activeStatus !== status) {
      setActiveStatus(status);
    }
  };

  const getNoTicketMessage = () => {
    if (activeStatus === 'open') {
      return 'No Open Tickets';
    }

    if (activeStatus === 'closed') {
      return 'No Closed Tickets';
    }
    return 'No Tickets';
  };

  const generateSinceDays = item => {
    if (item?.OpenSinceDays === 0) {
      return 'Today';
    }
    if (item?.OpenSinceDays === 1) {
      return '1 day';
    }
    return `${item?.OpenSinceDays} days`;
  };

  // Render Ticket Rows based on activeTab
  const renderTicket = () => {
    const colSpanCount = Object.entries(tableHeaders).length + 1;
    const tableData = data?.data;

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={colSpanCount}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              getNoTicketMessage()
            )}
          </td>
        </tr>
      );
    }

    return tableData?.map((item, index) => {
      if (activeTab === 'assignedToMe' || activeTab === 'myTickets') {
        return (
          <tr
            className="cursor-pointer"
            key={item?.TicketNo}
            style={{
              backgroundColor: activeRows?.includes(index)
                ? '#E0F2FE'
                : 'transparent',
            }}
            onClick={() =>
              navigate(
                activeTab === 'assignedToMe'
                  ? `/hr/HRtickets/${item.TicketNo}?fr=1`
                  : `/hr/tickets/${item.TicketNo}?fr=3`,
              )
            }
          >
            <td className="checkbox-col">
              <div className="form-check px-0">
                <Input
                  className="form-check-input blue-checkbox"
                  type="checkbox"
                  name="chk_child"
                  checked={activeRows?.includes(index)}
                  onChange={e => handleRowCheck(index, e.target.checked)}
                  onClick={e => e.stopPropagation()}
                />
              </div>
            </td>
            <td>
              <span
                onClick={() =>
                  navigate(
                    activeTab === 'assignedToMe'
                      ? `/hr/HRtickets/${item.TicketNo}?fr=1`
                      : `/tickets/${item.TicketNo}?fr=3`,
                  )
                }
                className="ticket-no text-decoration-underline p-0 px-3 cursor-pointer"
              >
                {item?.TicketNo}
              </span>
            </td>
            <td className="px-4">{item?.Subject}</td>
            <td className="px-4">{item?.Category}</td>
            {activeTab === 'assignedToMe' && (
              <td className="px-4">{item?.Requestor}</td>
            )}
            <td className="px-4">{item?.CreatedOn}</td>
            <td className="px-4">
              <Badge
                config={TICKET_STATUS_BADGE_STYLES[item.Status]}
                labelType="TitleCase"
                badgeType="pill"
              >
                {item.Status}
              </Badge>
            </td>
            {activeStatus !== 'closed' && (
              <td className="px-4">{generateSinceDays(item)}</td>
            )}
            {activeStatus === 'closed' && (
              <td className="px-4">{item?.StatusLastDate}</td>
            )}
          </tr>
        );
      }
      return (
        <tr
          className="cursor-pointer"
          key={item?.TicketNo}
          style={{
            backgroundColor: activeRows?.includes(index)
              ? '#E0F2FE'
              : 'transparent',
          }}
          onClick={() => navigate(`/hr/HRtickets/${item.TicketNo}?fr=2`)}
        >
          <td>
            <div className="form-check px-0">
              <Input
                className="form-check-input blue-checkbox"
                type="checkbox"
                name="chk_child"
                checked={activeRows?.includes(index)}
                onChange={e => handleRowCheck(index, e.target.checked)}
                onClick={e => e.stopPropagation()}
              />
            </div>
          </td>
          <td>
            <span
              onClick={() => navigate(`/hr/HRtickets/${item.TicketNo}?fr=2`)}
              className="ticket-no text-decoration-underline p-0 px-3 cursor-pointer"
            >
              {item?.TicketNo}
            </span>
          </td>
          <td className="px-4">{item?.Subject}</td>
          <td className="px-4">{item?.Category}</td>
          <td className="px-4">{item?.Requestor}</td>
          <td className="px-4">{item?.AssignedTo}</td>
          <td className="px-4">{item?.CreatedOn}</td>
          <td className="px-4">
            <Badge
              config={TICKET_STATUS_BADGE_STYLES[item.Status]}
              labelType="TitleCase"
              badgeType="pill"
            >
              {item.Status}
            </Badge>
          </td>
          {activeStatus !== 'closed' && (
            <td className="px-4">{generateSinceDays(item)}</td>
          )}
          {activeStatus === 'closed' && (
            <td className="px-4">{item?.StatusLastDate}</td>
          )}
        </tr>
      );
    });
  };

  // Hanlde Export to Excel based on activeTab and selected rows
  const handleExportExcel = async () => {
    const exportData = data?.data;
    if (!exportData || exportData.length === 0) {
      toast.error('No data found to export', {
        className: 'toast-error',
      });
      return;
    }
    const exportResponseData = await tableDataMutation.mutateAsync({
      ...pageRequest,
      pageNumber: 1,
      pageSize: 0,
    });

    let selectedData = exportResponseData?.data;

    if (activeRows?.length > 0) {
      selectedData = activeRows.map(index => exportResponseData?.data[index]);
      if (selectedData.length === 0) {
        toast.error('No selected data found to export', {
          className: 'toast-error',
        });
        return;
      }
    }

    if (activeTab === 'assignedToMe') {
      ExportUtil.downloadExcel(selectedData, {
        fileName: `Ticket_Enquiry_${FileUtil.getFileDateTimeStamp()}.xlsx`,
        columns: {
          TicketNo: 'Ticket ID',
          Subject: 'Subject',
          Category: 'Category',
          Requestor: 'Created By',
          CreatedOn: 'Created On',
          Status: 'Status',
          OpenSinceDays: 'Open Since',
          ...(activeStatus === 'closed' && { StatusLastDate: 'Closed On' }),
        },
      });
    } else if (activeTab === 'myTickets') {
      ExportUtil.downloadExcel(selectedData, {
        fileName: `Ticket_Enquiry_${FileUtil.getFileDateTimeStamp()}.xlsx`,
        columns: {
          TicketNo: 'Ticket ID',
          Subject: 'Subject',
          Category: 'Category',
          CreatedOn: 'Created On',
          Status: 'Status',
          OpenSinceDays: 'Open Since',
          ...(activeStatus === 'closed' && { StatusLastDate: 'Closed On' }),
        },
      });
    } else if (activeTab === 'allTickets') {
      ExportUtil.downloadExcel(selectedData, {
        fileName: `All_Tickets_Enquiry_${FileUtil.getFileDateTimeStamp()}.xlsx`,
        columns: {
          TicketNo: 'Ticket ID',
          Subject: 'Subject',
          Category: 'Category',
          Requestor: 'Created By',
          AssignedTo: 'Assigned To',
          CreatedOn: 'Created On',
          Status: 'Status',
          OpenSinceDays: 'Open Since',
          ...(activeStatus === 'closed' && { StatusLastDate: 'Closed On' }),
        },
      });
    }
  };

  const externalFiltersValidation = () => {
    if (
      externalFilters.ticketFromDate &&
      externalFilters.ticketToDate &&
      new Date(externalFilters?.ticketFromDate) >
        new Date(externalFilters?.ticketToDate)
    ) {
      toast.error('To Date should be greater than equal to From Date.');
      return false;
    }
    return true;
  };

  const getSubjectWidth = key => {
    let widths = {};

    if (activeStatus === 'open') {
      switch (activeTab) {
        case 'assignedToMe':
          widths = {
            TicketNo: '11%',
            Subject: '25%',
            Category: '14%',
            Requestor: '15%',
            CreatedOn: '15%',
            Status: '12%',
            OpenSinceDays: '12%',
          };
          break;
        case 'allTickets':
          widths = {
            TicketNo: '11%',
            Subject: '24%',
            Category: '13%',
            Requestor: '12%',
            AssignedTo: '13%',
            CreatedOn: '12%',
            Status: '12%',
            OpenSinceDays: '12%',
          };
          break;
        case 'myTickets':
          widths = {
            TicketNo: '11%',
            Subject: '43%',
            Category: '14%',
            CreatedOn: '12%',
            Status: '12%',
            OpenSinceDays: '12%',
          };
          break;
        default:
          break;
      }
    }

    if (activeStatus === 'closed') {
      switch (activeTab) {
        case 'assignedToMe':
          widths = {
            TicketNo: '11%',
            Subject: '25%',
            Category: '14%',
            Requestor: '15%',
            CreatedOn: '15%',
            Status: '12%',
            StatusLastDate: '12%',
          };
          break;
        case 'myTickets':
          widths = {
            TicketNo: '11%',
            Subject: '43%',
            Category: '14%',
            CreatedOn: '12%',
            Status: '12%',
            StatusLastDate: '12%',
          };
          break;
        case 'allTickets':
          widths = {
            TicketNo: '11%',
            Subject: '24%',
            Category: '13%',
            Requestor: '12%',
            AssignedTo: '13%',
            CreatedOn: '12%',
            Status: '12%',
            StatusLastDate: '12%',
          };
          break;
        default:
          break;
      }
    }
    return widths[key] || 'auto';
  };

  return (
    <div className="page-content hr-ticket-listing-page">
      <Card className="ticket-card">
        <CardHeader className="p-3 pb-2">
          <div className="d-flex flex-row justify-content-between align-items-center mb-2">
            <div className="fw-semibold fs-5">Tickets</div>
            <div className="d-flex flex-row align-items-center gap-2">
              <button
                type="button"
                className="custom-secondary-btn"
                onClick={handleExportExcel}
              >
                <img src={dlIcon} alt="Download" className="ticket-btn-icon" />
                <span className="ms-2">Download as Excel</span>
              </button>
              <button
                type="button"
                className="custom-secondary-btn"
                onClick={() => {
                  setModelName('reassignTicketModal');
                  setIsModalOpen(true);
                }}
                disabled={activeRows.length === 0}
              >
                <img
                  src={reassignIcon}
                  alt="Reassign HR"
                  className="ticket-btn-icon"
                />
                <span className="ms-2">Re-Assign</span>
              </button>
              <button
                type="button"
                className="custom-btn"
                onClick={toggleCreateTicketOffcanvas}
              >
                New Ticket
              </button>
            </div>
          </div>
          <div className="ticket-external-filter d-flex flex-row align-items-end justify-content-start gap-2">
            <Col lg={3} className="external-filter-col">
              <b>Search</b>
              <div className="search-box-right">
                <Input
                  type="text"
                  placeholder="Search Tickets"
                  onChange={e => {
                    setExternalFilters(prev => ({
                      ...prev,
                      ticketNo: e.target.value,
                    }));
                  }}
                  value={externalFilters?.ticketNo || ''}
                  className="external-filter-input"
                />
                <span className="search-icon ri-search-line" />
              </div>
            </Col>
            <Col lg={3} className="external-filter-col">
              <b>From Date</b>
              <DatePicker
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    ticketFromDate: e,
                  }))
                }
                className="form-control external-filter-input"
                value={externalFilters?.ticketFromDate || ''}
              />
            </Col>
            <Col lg={3} className="external-filter-col">
              <b>To Date</b>
              <DatePicker
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    ticketToDate: e,
                  }))
                }
                className="form-control external-filter-input"
                value={externalFilters?.ticketToDate || ''}
              />
            </Col>
            <Col className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="custom-btn"
                onClick={() => {
                  if (!externalFiltersValidation()) {
                    return;
                  }
                  applyExternalFilters(externalFilters);
                }}
              >
                Search
              </button>
            </Col>
          </div>
          <div className="mt-2">
            <TableFilterBubbles
              allFilters={pageRequest?.filters || []}
              clearSingleFilterValue={clearSingleFilterValue}
              clearAllFilters={resetFilters}
            />
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <div className="d-flex flex-row justify-content-between align-items-center">
            <ul className="nav nav-underline">
              <button
                type="button"
                className={`nav-link px-3 cursor-pointer ${activeTab === 'assignedToMe' && 'active'}`}
                onClick={() => {
                  toggleTab('assignedToMe');
                  setPageRequest(prev => ({
                    ...prev,
                    assignedTo: getAuthUser()?.employeeId,
                    requestor: null,
                  }));
                }}
                title="Tickets that are currently assigned to you for action."
              >
                Assigned To Me
              </button>
              {checkIfAdmin() && (
                <button
                  type="button"
                  className={`nav-link px-3 cursor-pointer ${activeTab === 'allTickets' && 'active'}`}
                  onClick={() => {
                    toggleTab('allTickets');
                    setPageRequest(prev => ({
                      ...prev,
                      assignedTo: null,
                      requestor: null,
                    }));
                  }}
                  title="View all tickets in the system, including resolved and assigned ones."
                >
                  All Tickets
                </button>
              )}
              <button
                type="button"
                className={`nav-link px-3 cursor-pointer ${activeTab === 'myTickets' && 'active'}`}
                onClick={() => {
                  toggleTab('myTickets');
                  setPageRequest(prev => ({
                    ...prev,
                    requestor: getAuthUser()?.employeeId,
                    assignedTo: null,
                  }));
                }}
                title="View tickets created by you"
              >
                My Tickets
              </button>
            </ul>
            <ul className="ticket-status-btns nav mx-3">
              <button
                type="button"
                className={`open-tickets-btn ${activeStatus === 'open' && 'active'}`}
                onClick={() => {
                  toggleStatus('open');
                  setPageRequest(prev => ({
                    ...prev,
                    ticketStatus: 'OPEN',
                  }));
                }}
              >
                Open
              </button>
              <button
                type="button"
                className={`closed-tickets-btn ${activeStatus === 'closed' && 'active'}`}
                onClick={() => {
                  toggleStatus('closed');
                  setPageRequest(prev => ({
                    ...prev,
                    ticketStatus: 'CLOSED', // Assuming 1 is for closed tickets
                  }));
                }}
              >
                Closed
              </button>
            </ul>
          </div>
          <div className="ticket-body-separator" />
          <TabContent activeTab={activeTab}>
            {Object.entries(TICKET_TABS).map(([, ticketTab]) => (
              <TabPane key={ticketTab} tabId={ticketTab} className="p-0">
                <div className="table-responsive m-0 hr-ticket-table-container">
                  <Table hover className="custom-table m-0 hr-ticket-table">
                    <thead>
                      <tr>
                        <th className="checkbox-col">
                          <div className="form-check px-0">
                            <Input
                              className="form-check-input blue-checkbox"
                              type="checkbox"
                              id="checkbox"
                              checked={
                                activeRows?.length > 0 &&
                                activeRows?.length === data?.data?.length
                              }
                              onChange={e => selectAllRows(e?.target?.checked)}
                            />
                          </div>
                        </th>
                        {Object.entries(tableHeaders).map(([key, label]) => (
                          <th
                            className="px-4"
                            key={key}
                            style={{
                              width: getSubjectWidth(key),
                            }}
                          >
                            <div className="d-flex flex-row align-items-center justify-content-start gap-1">
                              <div
                                className="p-0 cursor-pointer"
                                onClick={() => handleAutoSort(key)}
                              >
                                {label}
                              </div>
                              <TableSortDisplayArrow
                                direction={checkColumnIfSorted(key)}
                              />
                              <FilterDropdown
                                handleSetColumnName={() => {
                                  setColumnName(key);
                                }}
                                setColumnName={setColumnName}
                                columnData={columnData}
                                applyFilter={applyFilter}
                                columnName={columnName}
                                pageRequest={pageRequest}
                                setPageRequest={setPageRequest}
                                handleManualSort={handleManualSort}
                                isLoading={isLoadingColumn}
                              />
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>{renderTicket(activeTab)}</tbody>
                  </Table>
                </div>
              </TabPane>
            ))}
          </TabContent>
        </CardBody>
        <CardFooter className="py-2">
          <TablePagination
            nextPage={nextPage}
            prevPage={prevPage}
            totalPages={data?.totalPages}
            currentPage={data?.currentPage}
            hasPreviousPage={data?.hasPreviousPage}
            hasNextPage={data?.hasNextPage}
            getPageSize={getPageSize}
            setPageSize={setPageSize}
            totalCount={data?.totalCount}
          />
        </CardFooter>
      </Card>
      <CreateTicket
        isOpen={createTicketOffcanvas}
        toggle={toggleCreateTicketOffcanvas}
      />
      <ModalMultiWrapper
        modals={{
          reassignTicketModal: {
            component: ReassignTicket,
            props: {
              handleOnSubmit: async (payload, reset) => {
                if (activeRows.length === 0) {
                  toast.error(
                    'Please select at least one ticket to reassign.',
                    {
                      className: 'toast-error',
                    },
                  );
                  return;
                }
                let error = false;
                const payLoadList = activeRows.map(index => {
                  const ticketId = data?.data[index]?.TicketNo;
                  if (payload?.reassignTo === data?.data[index]?.Requestor) {
                    toast.error(
                      `Cannot reassign ticket #${ticketId} to yourself.`,
                      {
                        className: 'toast-error',
                      },
                    );
                    error = true;
                  }
                  if (payload?.reassignTo === data?.data[index]?.AssignedToCd) {
                    toast.error(
                      `Ticket #${ticketId} is already assigned to this user.`,
                      {
                        className: 'toast-error',
                      },
                    );
                    error = true;
                  }
                  return {
                    ticketNo: ticketId,
                    ...payload,
                  };
                });
                if (error) {
                  return;
                }
                await updateTicketListMutation.mutateAsync(payLoadList);
                reset();
                toast.success('Tickets reassigned successfully.', {
                  className: 'toast-success',
                });
                queryClient.invalidateQueries({
                  queryKey: ['ticketListing'],
                });
                setIsModalOpen(false);
                resetActiveRows();
              },
              isLoading: updateTicketListMutation?.isPending,
            },
          },
        }}
        isOpen={isModalOpen}
        toggle={() => setIsModalOpen(false)}
        selectedModal={modelName}
      />
    </div>
  );
}

export default HRTicketListing;
